import type { AbsenceListItem, AbsenceType, LeaveRegisterRow } from '@ip/shared-types';
import { eachDay, isWeekend, todayYmd } from '@/lib/hr';

/**
 * Pure aggregation helpers for the HR "Statystyki" tab (charts + the
 * per-employee table). Only APPROVED absences count; remote work is
 * informational (not an absence) and stays out of every absence metric.
 */

/** Chart series buckets: leave pool (urlop + na żądanie), sick (L4), everything else. */
export type AbsenceBucket = 'vacation' | 'sick' | 'other';

export function bucketOf(type: AbsenceType): AbsenceBucket | null {
  if (type === 'remote_work') return null;
  if (type === 'vacation' || type === 'vacation_on_demand') return 'vacation';
  if (type === 'sick') return 'sick';
  return 'other';
}

export interface MonthlyRow {
  month: number; // 1..12
  vacation: number;
  sick: number;
  other: number;
}

/**
 * Approved absence days per month of `year`. A multi-day absence contributes
 * ALL its days to the month of its start date — an approximation that keeps
 * the sum equal to the recorded day totals (splitting would need per-day
 * norms), fine for a trend chart.
 */
export function monthlyAbsences(absences: AbsenceListItem[], year: number): MonthlyRow[] {
  const rows: MonthlyRow[] = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1, vacation: 0, sick: 0, other: 0,
  }));
  for (const a of absences) {
    if (a.status !== 'approved') continue;
    const bucket = bucketOf(a.type);
    if (!bucket) continue;
    if (!a.dateFrom.startsWith(String(year))) continue;
    const month = Number(a.dateFrom.slice(5, 7));
    rows[month - 1][bucket] += a.days;
  }
  return rows;
}

export interface TopEmployeeRow {
  employeeId: string;
  employeeName: string;
  vacation: number;
  sick: number;
  other: number;
  total: number;
}

/** Top N employees by approved absence days (urlop + L4 + pozostałe), ties alphabetical. */
export function topEmployeesByAbsence(absences: AbsenceListItem[], limit = 10): TopEmployeeRow[] {
  const byEmployee = new Map<string, TopEmployeeRow>();
  for (const a of absences) {
    if (a.status !== 'approved') continue;
    const bucket = bucketOf(a.type);
    if (!bucket) continue;
    const row = byEmployee.get(a.employeeId) ?? {
      employeeId: a.employeeId, employeeName: a.employeeName,
      vacation: 0, sick: 0, other: 0, total: 0,
    };
    row[bucket] += a.days;
    row.total += a.days;
    byEmployee.set(a.employeeId, row);
  }
  return [...byEmployee.values()]
    .sort((a, b) => b.total - a.total || a.employeeName.localeCompare(b.employeeName, 'pl'))
    .slice(0, limit);
}

export interface EmployeeStatsRow {
  employeeId: string;
  employeeName: string;
  department: string | null;
  employmentType: string | null;
  /** Leave pool — null for non-UOP employees (no statutory pool). */
  entitlementDays: number | null;
  usedDays: number | null;
  pendingDays: number | null;
  remainingDays: number | null;
  onDemandUsedDays: number | null;
  sickDays: number;
  unavailabilityDays: number;
  remoteDays: number;
  /** Approved absence days ÷ working days of the year elapsed so far (%). */
  absenceRatePct: number;
}

/** Working days of `year` up to today (full year for past years). */
export function workingDaysElapsed(year: number, holidays: Map<string, string>): number {
  const today = todayYmd();
  const end = today < `${year}-12-31` ? today : `${year}-12-31`;
  if (end < `${year}-01-01`) return 0;
  return eachDay(`${year}-01-01`, end).filter((d) => !isWeekend(d) && !holidays.has(d)).length;
}

export function employeeStatsRows(
  register: LeaveRegisterRow[],
  absences: AbsenceListItem[],
  year: number,
  holidays: Map<string, string>,
): EmployeeStatsRow[] {
  const denominator = workingDaysElapsed(year, holidays);

  const perEmployee = new Map<string, { sick: number; unavailability: number; remote: number; absence: number }>();
  for (const a of absences) {
    if (a.status !== 'approved') continue;
    if (!a.dateFrom.startsWith(String(year))) continue;
    const agg = perEmployee.get(a.employeeId) ?? { sick: 0, unavailability: 0, remote: 0, absence: 0 };
    if (a.type === 'sick') agg.sick += a.days;
    if (a.type === 'unavailability_b2b') agg.unavailability += a.days;
    if (a.type === 'remote_work') agg.remote += a.days;
    if (bucketOf(a.type)) agg.absence += a.days;
    perEmployee.set(a.employeeId, agg);
  }

  return register.map((r) => {
    const agg = perEmployee.get(r.employeeId) ?? { sick: 0, unavailability: 0, remote: 0, absence: 0 };
    return {
      employeeId: r.employeeId,
      employeeName: `${r.lastName} ${r.firstName}`,
      department: r.department,
      employmentType: r.employmentType,
      entitlementDays: r.balance
        ? r.balance.entitlementDays + r.balance.carriedOverDays + r.balance.adjustmentDays
        : null,
      usedDays: r.balance?.usedDays ?? null,
      pendingDays: r.balance?.pendingDays ?? null,
      remainingDays: r.balance?.remainingDays ?? null,
      onDemandUsedDays: r.balance?.onDemandUsedDays ?? null,
      sickDays: agg.sick,
      unavailabilityDays: agg.unavailability,
      remoteDays: agg.remote,
      absenceRatePct: denominator > 0 ? (agg.absence / denominator) * 100 : 0,
    };
  });
}
